import {
  toWorkspaceAgentBoardMentionItem,
  type WorkspaceAgentBoardMentionSource,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentBoardMentionItem';
import {
  type WorkspaceAgentLibraryMentionSource,
  type WorkspaceAgentMentionTreeItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { flattenTree, type TreeRowPlacement } from '~/utils/tree/flattenTree';
import { type TreeLevel, type TreeNode } from '~/utils/tree/treeTypes';

/** What a node in the boards tree stands for: a library heading, or a board. */
export type WorkspaceAgentBoardTreeValue =
  | { board: WorkspaceAgentBoardMentionSource; kind: 'board' }
  | { kind: 'library'; library: WorkspaceAgentLibraryMentionSource };

/**
 * Browse and search build the same shape, so one flatten walks both: a library node heads a run of
 * roots, a board node holds whatever is currently under it.
 */
export type WorkspaceAgentBoardTreeNode = TreeNode<WorkspaceAgentBoardTreeValue>;

export type WorkspaceAgentBoardTreeLevel = TreeLevel<WorkspaceAgentBoardTreeValue>;

export type WorkspaceAgentBoardTreeExpansion = (nodeId: string, isExpandedByDefault: boolean) => boolean;

/** What the user has opened or closed, over the default rather than a snapshot of it. */
export type WorkspaceAgentBoardTreeExpansionOverrides = Record<string, boolean>;

/** One shared instance stands in for "no boards here", so it is handed out `readonly`. */
export const WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS: readonly WorkspaceAgentBoardMentionSource[] = [];

/** The run the library headings are numbered in — see the `groupKey` below. */
const WORKSPACE_AGENT_BOARD_TREE_LIBRARY_RUN = 'library';

const toWorkspaceAgentBoardTreeRow = (
  node: WorkspaceAgentBoardTreeNode,
  placement: TreeRowPlacement,
): WorkspaceAgentMentionTreeItem =>
  node.value.kind === 'library'
    ? {
        backgroundHex: node.value.library.color.backgroundHex,
        icon: node.value.library.icon,
        id: node.id,
        kind: 'library',
        title: node.value.library.title,
        ...placement,
      }
    : { ...toWorkspaceAgentBoardMentionItem(node.value.board), ...placement };

/** The tree as the one flat list the picker navigates by index. */
export const flattenWorkspaceAgentBoardTree = (
  roots: WorkspaceAgentBoardTreeLevel,
  isExpanded: WorkspaceAgentBoardTreeExpansion,
  /** The roots are a page of a longer list: their number is announced as unknown, not as loaded. */
  hasMoreRoots = false,
  hasMoreChildren: (nodeId: string) => boolean = () => false,
): WorkspaceAgentMentionTreeItem[] =>
  flattenTree(roots, {
    /**
     * At the root the library headings are announced inside their own `group` (the popup's
     * Libraries `fieldset`), so they are their own run of siblings — a position is only meaningful
     * within the group it is read in. Deeper levels are one run, as they are one list.
     */
    groupKey: (node, depth) =>
      depth === 0 && node.value.kind === 'library' ? WORKSPACE_AGENT_BOARD_TREE_LIBRARY_RUN : undefined,
    hasMoreChildren: (node) => hasMoreChildren(node.id),
    hasMoreRootNodes: hasMoreRoots,
    isExpanded: (node) => isExpanded(node.id, node.isExpandedByDefault),
    toRow: toWorkspaceAgentBoardTreeRow,
  });

/**
 * The boards whose children have to be fetched. Library rows are excluded — a library's roots come
 * from that library's own list — and the overrides passed in must be the browse ones: a row opened
 * under a query is drawn from the matches, not from a child fetch.
 */
export const toWorkspaceAgentBoardTreeOpenBoardIds = (
  overrides: WorkspaceAgentBoardTreeExpansionOverrides,
  libraryIds: ReadonlySet<string>,
): string[] => Object.keys(overrides).filter((nodeId) => overrides[nodeId] && !libraryIds.has(nodeId));

/** Browsed boards: children are whatever the open-board queries have returned so far. */
export const toWorkspaceAgentBoardTreeBrowseLevel = (
  boards: readonly WorkspaceAgentBoardMentionSource[],
  boardsByNodeId: Record<string, WorkspaceAgentBoardMentionSource[]>,
  /**
   * Browsing, opening a row is what fetches its sub-boards, so the server's count is what draws the
   * chevron. Under a query nothing is fetched, so a row opens only onto children already in the
   * tree — a chevron drawn from a count there would open onto nothing.
   */
  canFetchChildren = true,
): WorkspaceAgentBoardTreeLevel => {
  const level: WorkspaceAgentBoardTreeLevel = new Map();

  for (const board of boards) {
    const children = toWorkspaceAgentBoardTreeBrowseLevel(
      boardsByNodeId[board.id] ?? WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS,
      boardsByNodeId,
      canFetchChildren,
    );

    level.set(board.id, {
      children,
      hasChildren: canFetchChildren ? (board.directDescendantBoardCount ?? 0) > 0 : children.size > 0,
      id: board.id,
      isExpandedByDefault: false,
      value: { board, kind: 'board' },
    });
  }

  return level;
};

type ToWorkspaceAgentBoardTreeBrowseRootsParams = {
  boardsByNodeId: Record<string, WorkspaceAgentBoardMentionSource[]>;
  /** Libraries whose own roots request has answered, empty included. */
  fetchedLibraryIds: ReadonlySet<string>;
  generalLibrary: WorkspaceAgentLibraryMentionSource;
  generalRootBoards: readonly WorkspaceAgentBoardMentionSource[];
  hasNextPageNodeIds: ReadonlySet<string>;
  headingLibraries: readonly WorkspaceAgentLibraryMentionSource[];
  rootBoardsByLibraryId: Record<string, WorkspaceAgentBoardMentionSource[]>;
};

/**
 * Browse roots. A workspace with no named libraries has no Libraries chrome: general roots sit at
 * depth 0. With named libraries, Global is first in that group when it has roots, then named
 * libraries start closed. Global is omitted while empty. Never send `'general'` as `libraryIds`.
 */
export const toWorkspaceAgentBoardTreeBrowseRoots = ({
  boardsByNodeId,
  fetchedLibraryIds,
  generalLibrary,
  generalRootBoards,
  hasNextPageNodeIds,
  headingLibraries,
  rootBoardsByLibraryId,
}: ToWorkspaceAgentBoardTreeBrowseRootsParams): WorkspaceAgentBoardTreeLevel => {
  if (headingLibraries.length === 0) {
    return toWorkspaceAgentBoardTreeBrowseLevel(generalRootBoards, boardsByNodeId);
  }

  const roots: WorkspaceAgentBoardTreeLevel = new Map();

  if (generalRootBoards.length > 0 || hasNextPageNodeIds.has(generalLibrary.id)) {
    const children = toWorkspaceAgentBoardTreeBrowseLevel(generalRootBoards, boardsByNodeId);

    roots.set(generalLibrary.id, {
      children,
      hasChildren: children.size > 0 || hasNextPageNodeIds.has(generalLibrary.id),
      id: generalLibrary.id,
      isExpandedByDefault: true,
      value: { kind: 'library', library: generalLibrary },
    });
  }

  for (const library of headingLibraries) {
    if (library.id === generalLibrary.id) {
      continue;
    }

    const children = toWorkspaceAgentBoardTreeBrowseLevel(
      rootBoardsByLibraryId[library.id] ?? WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS,
      boardsByNodeId,
    );

    roots.set(library.id, {
      children,
      hasChildren: children.size > 0 || hasNextPageNodeIds.has(library.id) || !fetchedLibraryIds.has(library.id),
      id: library.id,
      isExpandedByDefault: false,
      value: { kind: 'library', library },
    });
  }

  return roots;
};

/**
 * Browsing and searching each resolve against their own overrides: a match is force-opened, and a
 * node the user collapsed while browsing must not hide the hits found under it.
 */
export const resolveWorkspaceAgentBoardTreeExpansion =
  (overrides: WorkspaceAgentBoardTreeExpansionOverrides): WorkspaceAgentBoardTreeExpansion =>
  (nodeId, isExpandedByDefault) =>
    overrides[nodeId] ?? isExpandedByDefault;

/**
 * Driven by the row's own state rather than a default recomputed here: search rows are created
 * open, so a default-derived toggle would spend the first click re-opening an open row.
 */
export const toggleWorkspaceAgentBoardTreeExpansion = (
  overrides: WorkspaceAgentBoardTreeExpansionOverrides,
  nodeId: string,
  isExpanded: boolean,
): WorkspaceAgentBoardTreeExpansionOverrides => ({ ...overrides, [nodeId]: !isExpanded });
